import { cva, type VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

/**
 * The uppercase micro-tag: a tiny read-only label pinned to something else — `GROUP` beside an
 * ingredient name, `3 ALLOWED`, `NEW`, `EXAMPLE`.
 *
 * ## Why this isn't a `Chip`
 *
 * `Chip variant="meta"` is the nearest neighbour and a genuinely different species. It's a
 * sentence-case label pill (cook time, cuisine) at `text-xs font-medium` with a border; this is an
 * uppercase micro-tag at `text-2xs font-bold` with letter-spacing and none. Folding one into the
 * other would mean a case/tracking/weight axis on `Chip` that applies to exactly one of its sizes,
 * on a component already carrying five variants and ten tones — and whose `meta` branch returns a
 * `<span>` while every other branch returns a `<button>`.
 *
 * A `Tag` is **always a `<span>` and never interactive**. That's the line: if it can be pressed, it's
 * a `Chip`. Keeping the two apart is what stops a read-only label drifting into looking tappable.
 *
 * ## `font-sans` is load-bearing
 *
 * Tags routinely sit inside `font-brand` headings — the food-profile rule row puts `GROUP` inside the
 * ingredient name — so without opting back into the sans stack a 10px uppercase tag renders in a
 * display serif. It's invisible until it's wrong, and it's the single best argument for this being a
 * component rather than a class string each call site remembers.
 *
 * ## One geometry
 *
 * Before this existed there were seven hand-rolled versions across five files, drifting in tracking
 * (`.06em`–`.13em`), padding (`px-[7px]`, `px-2`, `px-2.5`), weight and ink — including two identical
 * `GROUP` tags that disagreed with each other. The geometry is fixed here so `tone` is the only
 * decision a caller makes.
 */
const tagVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 font-sans text-2xs font-bold uppercase tracking-[0.08em]',
  {
    variants: {
      tone: {
        /** The default accent — categories, group membership, anything affirmative. */
        green: 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900 dark:text-brand-green-200',
        /**
         * An exception to whatever the thing normally does — "3 allowed" on a group that's otherwise
         * kept out. The ink is mixed from `--primary` rather than pinned to a hex, because amber at
         * full strength is unreadable on its own 13% wash and there's no darkened-amber token. A
         * `--primary-ink` token would retire the color-mix.
         */
        amber:
          'bg-primary/[0.13] text-[color-mix(in_srgb,oklch(var(--primary))_82%,#000)] dark:text-[color-mix(in_srgb,oklch(var(--primary))_88%,#fff)]',
        /** Quiet metadata that shouldn't compete — counts, states, "already added". */
        neutral: 'bg-muted text-muted-foreground',
        /** Hollow, for something that isn't real content: an `EXAMPLE`, a placeholder, a flag. */
        outline: 'border border-border bg-background text-faint'
      }
    },
    defaultVariants: { tone: 'neutral' }
  }
)

export interface TagProps extends VariantProps<typeof tagVariants> {
  /** Optional leading glyph (~10px). Sized by the caller. */
  icon?: ReactNode
  children: ReactNode
  className?: string
}

export function Tag({ tone, icon, children, className }: TagProps) {
  return (
    <span className={cn(tagVariants({ tone }), className)}>
      {icon}
      {children}
    </span>
  )
}
